import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ThemeSwitcherProvider } from "react-css-theme-switcher";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import ptBR from "antd/es/locale/pt_BR";
import moment from "moment";
import "moment/locale/pt-br";
import React, { lazy, Suspense } from "react";

import "@styles/global.less";

import { AppErrorBoundary } from "@components/ErrorBoundary/AppErrorBoundary";
import MobileNotSupported from "@components/MobileNotSupported";
import { GlobalStyle } from "@styles/globalStyle";

import { router } from "./router";

const isOfflineMode = import.meta.env.VITE_OFFLINE_MODE === "true";
const MockInitializer = isOfflineMode
  ? lazy(() => import("./mocks/MockInitializer"))
  : null;

import "overlayscrollbars/overlayscrollbars.css";
import "@goapice/react-datasets/dist/esm/index.css";
import "@goapice/react-guapo/dist/esm/index.css";
import "@goapice/react-sharing/dist/esm/index.css";

moment.locale("pt-br");

// ECharts locale init
void (async () => {
  try {
    const echarts = await import("echarts");
    const localePt = await import("echarts/i18n/langPT-br-obj");
    const localeWithChart = {
      ...localePt.default,
      series: {
        ...localePt.default.series,
        typeNames: {
          ...localePt.default.series.typeNames,
          chart: "Gráfico",
        },
      },
    };
    echarts.registerLocale("PT-br", localeWithChart as Parameters<typeof echarts.registerLocale>[1]);
  } catch (error) {
    console.error("[ECharts] Falha ao registrar locale PT-br:", error);
  }
})();

const themes = {
  dark: "/dark.css",
  light: "/light.css",
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500 && status !== 429) return false;
        return true;
      },
    },
  },
});

// StrictMode desabilitado — mesmo comportamento do projeto original
// (next.config.js: reactStrictMode: false). Pode ser reabilitado após validação.
function App(): JSX.Element {
  const content = (
    <AppErrorBoundary>
      <ConfigProvider locale={ptBR}>
        <ThemeSwitcherProvider themeMap={themes} defaultTheme="light">
          <QueryClientProvider client={queryClient}>
            <>
              <GlobalStyle />
              <MobileNotSupported>
                <RouterProvider router={router} />
              </MobileNotSupported>
            </>
          </QueryClientProvider>
        </ThemeSwitcherProvider>
      </ConfigProvider>
    </AppErrorBoundary>
  );

  if (MockInitializer) {
    return (
      <Suspense fallback={null}>
        <MockInitializer>{content}</MockInitializer>
      </Suspense>
    );
  }

  return content;
}

createRoot(document.getElementById("root")!).render(<App />);
